/**
 * Iris's mark on a field she filled from the call.
 *
 * Her orb — the same gradient the chip and the home hero use, so the thing
 * beside "Job type" is recognisably the thing that was listening — plus the
 * word, because an orb alone is a decoration until it is named. It reads as
 * "Iris" at a glance and explains itself on hover.
 *
 * It is deliberately NOT a badge with a border and a fill. This sits next to a
 * field label inside a form of forty labels; a chip at every one of them would
 * out-weigh the labels themselves. The orb carries the colour, the word carries
 * the meaning, and nothing else is added.
 *
 * The mark is transient by design: the caller shows it only while the field
 * still holds what Iris wrote. The moment the agent types over it the value is
 * theirs and the mark goes — see `irisFilledFields` in JobForm.
 */

import { IrisOrb } from '@/components/atoms/IrisOrb';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/atoms/tooltip';
import { cn } from '@/lib/utils';

/**
 * A field Iris is ABOUT to fill.
 *
 * The mark used to arrive with the value, which meant the first news an agent
 * had of Iris working on a field was their own work being replaced by it: they
 * opened the slot picker, and a moment later the selection was somebody else's.
 * A provenance mark that only ever appears afterwards cannot tell anyone what
 * to leave alone.
 *
 * So the mark comes FIRST and the value follows. The gap is short — long enough
 * to be seen and to be claimed, not long enough to feel like waiting — and
 * clicking into the field during it is what cancels the write (see
 * `irisAnnounce` in JobForm). NOT-672.
 *
 * An object rather than a string, so it cannot be mistaken for the words Iris
 * heard: every other mark value is either `true` or the quote itself.
 */
export const IRIS_FILLING = { filling: true } as const;

/**
 * What one field's mark can say: the words Iris heard, a bare `true` when there
 * is nothing worth quoting, or {@link IRIS_FILLING} while the value is still on
 * its way.
 */
export type IrisFieldMark = string | true | typeof IRIS_FILLING;

/**
 * The tint a marked control wears — pale amber for a value that landed, the
 * same amber breathing for one that has been announced and has not.
 *
 * One helper rather than `mark && 'iris-filled'` at every call site: the two
 * states have to stay in step across three components and a dozen controls,
 * and a control that showed the landed tint while the value was still coming
 * would be claiming Iris had written something she had not.
 */
export function irisFieldClass(mark?: IrisFieldMark): string | undefined {
  if (!mark) return undefined;
  return mark === IRIS_FILLING ? 'iris-filling' : 'iris-filled';
}

/**
 * The mark for a field whose value has been announced but has not landed.
 *
 * Says what is happening and what to do about it: the agent who wants this
 * field can take it by clicking into it, and Iris stands down.
 */
export function IrisFillingMark({ className }: { className?: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          className="inline-flex h-4 cursor-help items-center rounded-sm leading-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring"
          aria-label="Iris is filling this field from the call"
        >
          <span
            className={cn(
              'inline-flex select-none items-center gap-1 align-middle text-[10.5px] font-medium leading-none text-muted-foreground',
              className,
            )}
          >
            <IrisOrb className="iris-orb-beat size-2.5" />
            Iris is filling…
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[240px]">
        <p className="font-medium">Iris is filling this</p>
        <p className="mt-0.5 text-background/80">Click into the field to keep it yours.</p>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * One field's mark, in whichever state it is in. Call sites pass the mark and
 * get the right one — there is no third place that has to remember which
 * component goes with which state.
 */
export function IrisMark({ mark, className }: { mark?: IrisFieldMark; className?: string }) {
  if (!mark) return null;
  if (mark === IRIS_FILLING) return <IrisFillingMark className={className} />;
  return <IrisFilledMark heard={typeof mark === 'string' ? mark : undefined} className={className} />;
}

export function IrisFilledMark({
  /** What Iris heard, shown on hover so the agent can check it against the call. */
  heard,
  className,
}: {
  heard?: string;
  className?: string;
}) {
  const mark = (
    <span
      className={cn(
        'inline-flex select-none items-center gap-1 align-middle text-[10.5px] font-medium leading-none text-muted-foreground',
        className,
      )}
    >
      <IrisOrb className="size-2.5" />
      Iris
    </span>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          // The tooltip needs a focusable, hoverable host; the mark itself is
          // decoration. `tabIndex={0}` so a keyboard can reach the explanation
          // — an agent who cannot see the orb still needs to know the value
          // came off the call rather than out of the record.
          tabIndex={0}
          /*
           * Exactly the height of the label it sits beside — `h-4`, matching
           * the Label atom's 16px line box, with the leading killed so the
           * span does not carry one of its own.
           *
           * Without this the trigger inherited the body's 1.5 leading and
           * stood 24px tall, which made a MARKED label row 8px taller than an
           * unmarked one. In a two-up row — Job Type beside Priority, Issue
           * beside Tags — only one side grew, and the two controls stopped
           * lining up. A provenance mark must not move the form.
           */
          className="inline-flex h-4 cursor-help items-center rounded-sm leading-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring"
          aria-label={
            heard ? `Filled by Iris from the call: ${heard}` : 'Filled by Iris from the call'
          }
        >
          {mark}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[240px]">
        <p className="font-medium">Filled from the call</p>
        {heard ? <p className="mt-0.5 text-background/80">Iris heard “{heard}”</p> : null}
        <p className="mt-1 text-background/80">Type over it to make it yours.</p>
      </TooltipContent>
    </Tooltip>
  );
}
